import React, {useState} from "react";
import {
  AreaChart,
  Area,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  useAnalyticsSummary,
  useAnalyticsDaily,
  useArtifactStats,
  useRefreshArtifactStats,
  useRendererHealth,
} from "hooks/useAnalytics";

import "./index.css";

const RANGES = [
  {label: "7d", days: 7},
  {label: "30d", days: 30},
  {label: "90d", days: 90},
  {label: "1y", days: 365},
];

function formatNumber(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function TrendIndicator({changePct}) {
  if (changePct === null || changePct === undefined) {
    return <span className={"admin-analytics-card-trend admin-analytics-trend-neutral"}>--</span>;
  }
  const isUp = changePct > 0;
  const arrow = isUp ? "\u2191" : changePct < 0 ? "\u2193" : "";
  const cls = isUp
    ? "admin-analytics-trend-up"
    : changePct < 0
      ? "admin-analytics-trend-down"
      : "admin-analytics-trend-neutral";
  return (
    <span className={`admin-analytics-card-trend ${cls}`}>
      {arrow} {Math.abs(changePct)}%
    </span>
  );
}

/**
 * Say which accounts the numbers leave out. Every figure on this page comes
 * from a filtered query, so a reader who does not know the roster would read a
 * drop in the team's own traffic as a drop in use.
 */
function ExclusionNote({emails}) {
  if (!emails?.length) return null;
  const names = emails.map((email) => email.split("@")[0]).join(", ");
  return (
    <p className="admin-analytics-exclusion-note" title={emails.join(", ")}>
      Excludes {emails.length} {emails.length === 1 ? "account" : "accounts"}: {names}
    </p>
  );
}

function UserActivityCards({summary, daily}) {
  const highlightCards = daily
    ? [
        {label: "People Saving", value: formatNumber(daily.distinct_savers)},
        {label: "People Exporting", value: formatNumber(daily.distinct_exporters)},
      ]
    : [];
  const summaryCards = summary
    ? [
        {label: "Sessions", metric: summary.sessions},
        {label: "Exports", metric: summary.exports},
      ]
    : [];
  return (
    <div className="admin-analytics-cards-grid">
      {highlightCards.map((c) => (
        <div key={c.label} className="admin-analytics-card admin-analytics-card-highlight">
          <span className="admin-analytics-card-label">{c.label}</span>
          <span className="admin-analytics-card-value">{c.value}</span>
          <span className="admin-analytics-card-trend admin-analytics-trend-neutral">
            unique users
          </span>
        </div>
      ))}
      {summaryCards.map((c) => (
        <div key={c.label} className="admin-analytics-card">
          <span className="admin-analytics-card-label">{c.label}</span>
          <span className="admin-analytics-card-value">{formatNumber(c.metric.current)}</span>
          <TrendIndicator changePct={c.metric.change_pct} />
        </div>
      ))}
    </div>
  );
}

function SystemHealthCards({summary}) {
  if (!summary) return null;
  const cards = [
    {label: "API Calls", metric: summary.api_calls},
    {label: "Error Rate", metric: summary.error_rate, suffix: "%"},
  ];
  return (
    <div className="admin-analytics-cards-grid">
      {cards.map((c) => (
        <div key={c.label} className="admin-analytics-card">
          <span className="admin-analytics-card-label">{c.label}</span>
          <span className="admin-analytics-card-value">
            {c.suffix ? `${c.metric.current}${c.suffix}` : formatNumber(c.metric.current)}
          </span>
          <TrendIndicator changePct={c.metric.change_pct} />
        </div>
      ))}
    </div>
  );
}

const CHART_TOOLTIP_STYLE = {
  backgroundColor: "#262626",
  border: "1px solid #404040",
  borderRadius: "8px",
  fontSize: "12px",
};

function DailyChart({data}) {
  return (
    <div className="admin-analytics-chart-section">
      <h3 className="admin-analytics-chart-title">Activity Over Time</h3>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data} margin={{top: 4, right: 4, bottom: 0, left: 0}}>
          <defs>
            <linearGradient id="colorExports" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4c8bf5" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#4c8bf5" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#2b2d30" />
          <XAxis
            dataKey="date"
            tick={{fill: "#737373", fontSize: 11}}
            tickLine={false}
            axisLine={{stroke: "#404040"}}
            tickFormatter={(d) => {
              const date = new Date(d + "T00:00:00");
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
          />
          <YAxis
            tick={{fill: "#737373", fontSize: 11}}
            tickLine={false}
            axisLine={{stroke: "#404040"}}
            width={40}
          />
          <Tooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            labelStyle={{color: "#fafafa"}}
            itemStyle={{color: "#d4d4d4"}}
          />
          <Area
            type="monotone"
            dataKey="exports"
            stroke="#4c8bf5"
            fill="url(#colorExports)"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="sessions"
            stroke="#8b5cf6"
            fill="url(#colorSessions)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

const SOURCE_FILTERS = [
  {key: "all", label: "All", match: () => true},
  {key: "client", label: "User actions", match: (row) => row.source === "client"},
  {key: "server", label: "Server activity", match: (row) => row.source === "server"},
];

function TopEventsTable({data}) {
  const [sourceFilter, setSourceFilter] = useState("all");
  if (!data || data.length === 0) {
    return <div className="admin-analytics-empty-state">No events recorded yet.</div>;
  }
  const matcher = SOURCE_FILTERS.find((f) => f.key === sourceFilter).match;
  const filtered = data.filter(matcher);
  return (
    <div className="admin-analytics-events-section">
      <div className="admin-analytics-filter-pills">
        {SOURCE_FILTERS.map((f) => (
          <button
            key={f.key}
            className={`admin-analytics-filter-pill ${sourceFilter === f.key ? "admin-analytics-filter-pill-active" : ""}`}
            onClick={() => setSourceFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="admin-analytics-empty-state">No events match the selected filter.</div>
      ) : (
        <div className="admin-analytics-table-wrap">
          <table className="admin-analytics-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Source</th>
                <th className="admin-analytics-numeric-cell">Count</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={`${row.event}-${row.source}`}>
                  <td>{row.event}</td>
                  <td>
                    <span
                      className={`admin-analytics-source-badge ${row.source === "client" ? "admin-analytics-source-client" : "admin-analytics-source-server"}`}
                    >
                      {row.source}
                    </span>
                  </td>
                  <td className="admin-analytics-numeric-cell">{row.count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TopUsersTable({data}) {
  if (!data || data.length === 0) {
    return (
      <div className="admin-analytics-empty-state">
        No per-user activity yet. Events created before user attribution shipped do not appear here.
      </div>
    );
  }
  return (
    <div className="admin-analytics-table-wrap">
      <table className="admin-analytics-table">
        <thead>
          <tr>
            <th>User</th>
            <th className="admin-analytics-numeric-cell">Sessions</th>
            <th className="admin-analytics-numeric-cell">Saves</th>
            <th className="admin-analytics-numeric-cell">Exports</th>
            <th className="admin-analytics-numeric-cell">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.email}>
              <td>{row.email}</td>
              <td className="admin-analytics-numeric-cell">{row.sessions.toLocaleString()}</td>
              <td className="admin-analytics-numeric-cell">{row.saves.toLocaleString()}</td>
              <td className="admin-analytics-numeric-cell">{row.exports.toLocaleString()}</td>
              <td className="admin-analytics-numeric-cell">{row.total.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ByProductChannelTable({data}) {
  if (!data || data.length === 0) {
    return (
      <div className="admin-analytics-empty-state">
        No product/channel activity yet. Events created before client enrichment shipped do not
        appear here.
      </div>
    );
  }
  return (
    <div className="admin-analytics-table-wrap">
      <table className="admin-analytics-table">
        <thead>
          <tr>
            <th>Product</th>
            <th>Channel</th>
            <th className="admin-analytics-numeric-cell">Sessions</th>
            <th className="admin-analytics-numeric-cell">Saves</th>
            <th className="admin-analytics-numeric-cell">Exports</th>
            <th className="admin-analytics-numeric-cell">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={`${row.product}-${row.channel}`}>
              <td>{row.product}</td>
              <td>{row.channel}</td>
              <td className="admin-analytics-numeric-cell">{row.sessions.toLocaleString()}</td>
              <td className="admin-analytics-numeric-cell">{row.saves.toLocaleString()}</td>
              <td className="admin-analytics-numeric-cell">{row.exports.toLocaleString()}</td>
              <td className="admin-analytics-numeric-cell">{row.total.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ByChannelTable({data}) {
  if (!data || data.length === 0) {
    return <div className="admin-analytics-empty-state">No channel data available.</div>;
  }
  return (
    <div className="admin-analytics-table-wrap">
      <table className="admin-analytics-table">
        <thead>
          <tr>
            <th>Channel</th>
            <th className="admin-analytics-numeric-cell">Exports</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.channel}>
              <td>{row.channel}</td>
              <td className="admin-analytics-numeric-cell">{row.count.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApiHealthTable({data}) {
  if (!data || data.length === 0) {
    return <div className="admin-analytics-empty-state">No API health data available.</div>;
  }
  return (
    <div className="admin-analytics-table-wrap">
      <table className="admin-analytics-table">
        <thead>
          <tr>
            <th>Endpoint</th>
            <th className="admin-analytics-numeric-cell">Calls</th>
            <th className="admin-analytics-numeric-cell">Errors</th>
            <th className="admin-analytics-numeric-cell">Avg (ms)</th>
            <th className="admin-analytics-numeric-cell">P95 (ms)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.event}>
              <td>{row.event}</td>
              <td className="admin-analytics-numeric-cell">{row.count.toLocaleString()}</td>
              <td className="admin-analytics-numeric-cell">{row.errors}</td>
              <td className="admin-analytics-numeric-cell">{row.avg_ms}</td>
              <td className="admin-analytics-numeric-cell">{row.p95_ms}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const EXPORT_OUTCOME_LABELS = {
  "neurobaker.export.completed": "Completed",
  "neurobaker.export.server_failed": "Server fallback",
  "neurobaker.export.failed": "Failed",
};

function ExportHealthTable({data}) {
  if (!data || data.length === 0) {
    return <div className="admin-analytics-empty-state">No instrumented exports yet.</div>;
  }
  return (
    <div className="admin-analytics-table-wrap">
      <table className="admin-analytics-table">
        <thead>
          <tr>
            <th>Outcome</th>
            <th>Renderer</th>
            <th>Selection</th>
            <th>Build</th>
            <th className="admin-analytics-numeric-cell">Count</th>
            <th className="admin-analytics-numeric-cell">Avg (ms)</th>
            <th className="admin-analytics-numeric-cell">P95 (ms)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={`${row.event}-${row.renderer}-${row.selection}-${row.build_version}-${row.build_sha}`}
            >
              <td>{EXPORT_OUTCOME_LABELS[row.event] ?? row.event}</td>
              <td>{row.renderer}</td>
              <td>{row.selection}</td>
              <td>
                {row.build_version} ({row.build_sha.slice(0, 8)})
              </td>
              <td className="admin-analytics-numeric-cell">{row.count.toLocaleString()}</td>
              <td className="admin-analytics-numeric-cell">{row.avg_ms}</td>
              <td className="admin-analytics-numeric-cell">{row.p95_ms}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function formatUptime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return `${h}h ${m}m`;
}

function RendererHealthPanel({data}) {
  if (!data) {
    return <div className="admin-analytics-empty-state">Renderer not available</div>;
  }
  const {pool, memory, uptime} = data;
  const containerMemory = memory?.container;
  const memoryValue = containerMemory?.usageBytes ?? memory?.rss ?? 0;
  const memoryDetail = containerMemory?.limitBytes
    ? `${containerMemory.usagePercent}% of ${formatBytes(containerMemory.limitBytes)}; process RSS ${formatBytes(memory?.rss ?? 0)}`
    : `process RSS ${formatBytes(memory?.rss ?? 0)}`;
  return (
    <div className="admin-analytics-renderer-panel">
      <div className="admin-analytics-cards-grid">
        <div className="admin-analytics-card">
          <span className="admin-analytics-card-label">Pool Size</span>
          <span className="admin-analytics-card-value">{pool?.pool?.size ?? 0}</span>
          <span className="admin-analytics-card-trend admin-analytics-trend-neutral">
            {pool?.pool?.free ?? 0} free / {pool?.pool?.inUse ?? 0} busy
          </span>
        </div>
        <div className="admin-analytics-card">
          <span className="admin-analytics-card-label">Queue</span>
          <span className="admin-analytics-card-value">{pool?.queue?.current ?? 0}</span>
          <span className="admin-analytics-card-trend admin-analytics-trend-neutral">
            max seen: {pool?.queue?.maxSeen ?? 0}
          </span>
        </div>
        <div className="admin-analytics-card">
          <span className="admin-analytics-card-label">Renders</span>
          <span className="admin-analytics-card-value">
            {formatNumber(pool?.requests?.total ?? 0)}
          </span>
          <span
            className={`admin-analytics-card-trend ${(pool?.requests?.errors ?? 0) > 0 ? "admin-analytics-trend-down" : "admin-analytics-trend-neutral"}`}
          >
            {pool?.requests?.errors ?? 0} errors
          </span>
        </div>
        <div className="admin-analytics-card">
          <span className="admin-analytics-card-label">Container Memory</span>
          <span className="admin-analytics-card-value">{formatBytes(memoryValue)}</span>
          <span className="admin-analytics-card-trend admin-analytics-trend-neutral">
            {memoryDetail}
          </span>
        </div>
      </div>
      <div className="admin-analytics-table-wrap">
        <table className="admin-analytics-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Uptime</td>
              <td>{formatUptime(uptime ?? 0)}</td>
            </tr>
            <tr>
              <td>PID</td>
              <td>{data.pid}</td>
            </tr>
            <tr>
              <td>Build</td>
              <td>
                {data.build?.version ?? "unknown"} ({data.build?.sha?.slice(0, 8) ?? "unknown"})
              </td>
            </tr>
            <tr>
              <td>Container Working Set</td>
              <td>{formatBytes(containerMemory?.workingSetBytes ?? memory?.rss ?? 0)}</td>
            </tr>
            <tr>
              <td>Bun Heap</td>
              <td>
                {formatBytes(memory?.heapUsed ?? 0)} / {formatBytes(memory?.heapTotal ?? 0)}
              </td>
            </tr>
            <tr>
              <td>Avg Queue Wait</td>
              <td>{(pool?.queueWait?.avgMs ?? 0).toFixed(0)} ms</td>
            </tr>
            <tr>
              <td>Max Queue Wait</td>
              <td>{(pool?.queueWait?.maxMs ?? 0).toFixed(0)} ms</td>
            </tr>
            <tr>
              <td>Pages Recycled</td>
              <td>{pool?.recycled ?? 0}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatRate(signal) {
  if (!signal || signal.rate === null || signal.rate === undefined) return "\u2013";
  return `${signal.rate}%`;
}

/**
 * The interval belongs in a tooltip, not on the page. It is what decides
 * trend-versus-noise, but reading it off the screen is nobody's job.
 */
/**
 * Users behind a signal, with no rate.
 *
 * Every build in a phase ships the same artwork, so a build is not a thing to
 * judge — it is a marker of when the switching accumulated. Printing a
 * percentage per build invites a comparison that cannot mean anything: the
 * builds differ only in how long they were exposed inside the phase window.
 */
function formatUsers(signal) {
  if (!signal || !signal.sample) return "—";
  return signal.users.toLocaleString();
}

function rateDetail(signal) {
  if (!signal || signal.rate === null) return undefined;
  return `95% interval ${signal.ci_low}-${signal.ci_high} pp, ${signal.users.toLocaleString()} of ${signal.sample.toLocaleString()} users`;
}

function formatCount(signal) {
  if (!signal || !signal.sample) return null;
  return `${signal.users.toLocaleString()} / ${signal.sample.toLocaleString()}`;
}

/**
 * Trend per product across release phases.
 *
 * Only the release channel is plotted, and the axis is the major line. Artwork
 * changes at phase boundaries and never inside a phase, so the meaningful
 * movement is 2026.1 Release -> 2026.2 Release; the builds within a phase all
 * carry the same splash and belong in the table, not here.
 *
 * EAP and RC are deliberately absent. Their audiences are not the release
 * audience — `nosplash` runs 8-28% there against 0.5-1% in release — so a line
 * through EAP, RC and release points draws the channel, not the artwork.
 *
 * Small multiples rather than one chart: rates differ by an order of magnitude
 * between products (IntelliJ ~0.15%, PyCharm ~0.006%) and a shared axis would
 * flatten the small ones onto zero. They are not comparable across products
 * anyway — not every product reports every signal.
 */
function releaseTrendCharts(rows) {
  const releases = (rows ?? []).filter(
    (row) =>
      row.channel === "release" &&
      row.window_complete &&
      row.signals?.simplified_switched?.rate !== null
  );
  const products = [...new Set(releases.map((row) => row.product))].sort();
  return products
    .map((product) => {
      const series = releases
        .filter((row) => row.product === product)
        .slice()
        .sort((a, b) => a.major_version.localeCompare(b.major_version, undefined, {numeric: true}))
        .map((row) => {
          const signal = row.signals.simplified_switched;
          return {
            label: row.major_version,
            rate: signal.rate,
            band: [signal.ci_low, signal.ci_high],
          };
        });
      return {product, series};
    })
    .filter((chart) => chart.series.length >= 2);
}

function SplashTrendCharts({rows}) {
  const charts = releaseTrendCharts(rows);
  if (charts.length === 0) return null;
  return (
    <>
      <div className="admin-analytics-panel-note admin-analytics-panel-note-prose">
        Share of users switching to the simplified splash in each release phase, by major line. The
        shaded band is the 95% interval: a point inside the previous band is not a change.
      </div>
      {/* Cells, not panels. Nesting a bordered section inside a bordered section
          drew a box in a box and made three small charts read as three cards. */}
      <div className="admin-analytics-chart-grid" style={{marginTop: 22}}>
        {charts.map(({product, series}) => (
          <div key={product} className="admin-analytics-chart-cell">
            <div className="admin-analytics-chart-cell-head">
              <span className="admin-analytics-chart-cell-name">{product}</span>
              <span className="admin-analytics-chart-cell-value">
                {series[series.length - 1].rate}%
              </span>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <ComposedChart data={series} margin={{top: 8, right: 12, bottom: 0, left: 0}}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={{fontSize: 11, fill: "rgba(255,255,255,0.5)"}} />
                <YAxis
                  tick={{fontSize: 11, fill: "rgba(255,255,255,0.5)"}}
                  tickFormatter={(value) => `${value}%`}
                  width={54}
                />
                <Tooltip
                  contentStyle={{background: "#1b1b1f", border: "1px solid rgba(255,255,255,0.12)"}}
                  formatter={(value, name) =>
                    name === "band"
                      ? [`${value[0]}-${value[1]} pp`, "95% interval"]
                      : [`${value}%`, "switched"]
                  }
                />
                <Area
                  dataKey="band"
                  stroke="none"
                  fill="rgba(110,140,255,0.18)"
                  isAnimationActive={false}
                />
                <Line
                  dataKey="rate"
                  stroke="#6e8cff"
                  strokeWidth={2}
                  dot={{r: 3}}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </>
  );
}

const VERDICTS = {
  // A rising share of users switching away is bad news, so the colours invert.
  rising: {label: "trend: rising", className: "admin-analytics-trend-down"},
  falling: {label: "trend: falling", className: "admin-analytics-trend-up"},
  noise: {label: "no trend (noise)", className: "admin-analytics-trend-neutral"},
  // Not "nothing happened": the comparison cannot answer the question, because
  // the two phases were seen by different audiences.
  audience_changed: {
    label: "audience changed — not comparable",
    className: "admin-analytics-trend-neutral",
  },
};

/**
 * The conclusion, not the arithmetic. The intervals are computed server-side
 * and only their verdict is shown: WebStorm going from 3 users to 16 between
 * two releases looks like a 3.5x jump as a number and is noise as a verdict.
 */
function Verdict({comparison, previousVersion}) {
  if (!comparison) {
    return (
      <span className="admin-analytics-card-trend admin-analytics-trend-neutral">
        {previousVersion ? `vs ${previousVersion}` : "no earlier release measured"}
      </span>
    );
  }
  const {label, className} = VERDICTS[comparison.verdict] ?? VERDICTS.noise;
  const detail = [
    `${comparison.delta > 0 ? "+" : ""}${comparison.delta} pp vs ${previousVersion}`,
    `95% intervals ${comparison.current_ci.join("-")} vs ${comparison.previous_ci.join("-")}`,
    comparison.low_count ? "fewer than 5 users on one side" : null,
  ]
    .filter(Boolean)
    .join(" | ");
  return (
    <span className={`admin-analytics-card-trend ${className}`} title={detail}>
      {label}
      {comparison.low_count && " *"}
    </span>
  );
}

/**
 * A warning about how the phase was grouped, not about the numbers.
 *
 * A phase whose splash captions were read is one artwork by construction, so
 * there is nothing to warn about. A phase grouped from the milestone the feed
 * declared is a guess: the milestone and the shipped artwork disagree in
 * practice — 2026.2's RC shipped the Beta image, 2025.2's Beta shipped the
 * release image — so such a sample may be mixing two artworks without anyone
 * being able to tell.
 *
 * Products whose artwork sits in a closed repository can only ever be grouped
 * that way, which is exactly why it is worth naming: the fix is for CoverBaker
 * to record the artwork per build, not for a reader to assume.
 */
function SplashWarning({products}) {
  const rows = products ?? [];
  const unread = [
    ...new Set(rows.filter((row) => row.artwork?.proof === "none").map((row) => row.product)),
  ];
  const proxied = [
    ...new Set(rows.filter((row) => row.artwork?.proof === "proxy").map((row) => row.product)),
  ];
  if (unread.length === 0 && proxied.length === 0) return null;

  const parts = [];
  if (unread.length)
    parts.push(
      `${unread.join(", ")}: no splash was read, so the grouping is the milestone the release feed declared — which has been wrong twice where it could be checked (2026.2 RC shipped the Beta artwork, 2025.2 Beta shipped the release artwork).`
    );
  if (proxied.length)
    parts.push(
      `${proxied.join(", ")}: grouped from the IntelliJ Community splash on the same build tags. The artwork drops of a branch land in one commit per milestone, so the grouping carries over, but the image read is not this product's.`
    );

  return (
    <span className="admin-analytics-source-badge" title={parts.join(" ")}>
      {unread.length
        ? `artwork unread: ${unread.join(", ")}`
        : `artwork by proxy: ${proxied.join(", ")}`}
    </span>
  );
}

function Chevron({open = false, color = "#737373", size = 12}) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d={open ? "M2.5 4.5 6 8l3.5-3.5" : "M4.5 2.5 8 6l-3.5 3.5"}
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** A percentage with only as many decimals as it carries. */
function pct(value) {
  return `${Number(value.toFixed(3))}%`;
}

/**
 * Axis bounds a reader can hold in their head.
 *
 * Padding a raw min/max gives ticks like 0.318% and 0.424%, which look like
 * measurements. Widening to a round step costs a little whitespace and makes
 * the axis read as a scale.
 */
function niceBounds(rawLo, rawHi) {
  const span = rawHi - rawLo || Math.abs(rawHi) || 1;
  const magnitude = 10 ** Math.floor(Math.log10(span / 2));
  const step =
    [1, 2, 2.5, 5, 10].map((factor) => factor * magnitude).find((size) => span / size <= 4) ??
    magnitude * 10;
  return {
    lo: Math.max(0, Math.floor(rawLo / step) * step),
    hi: Math.ceil(rawHi / step) * step,
  };
}

/**
 * Two phases and their 95% intervals on one axis.
 *
 * The verdict is a statement about these two bars — overlapping means the
 * difference is inside sampling error. Printing only the word asks the reader
 * to take that on faith; drawing the bars costs one row and settles it.
 */
function IntervalPlot({previous, current, labels, tone = "accent", width = 560}) {
  if (!previous || !current) return null;
  if (previous.rate === null || current.rate === null) return null;

  const lo = Math.min(previous.ci_low, current.ci_low);
  const hi = Math.max(previous.ci_high, current.ci_high);
  const pad = (hi - lo) * 0.3 || Math.max(hi * 0.3, 0.001);
  const {lo: min, hi: max} = niceBounds(Math.max(0, lo - pad), hi + pad);
  const plotX = 70;
  const plotW = width - plotX - 62;
  const x = (value) => plotX + ((value - min) / (max - min)) * plotW;

  const warn = tone === "warn";
  const dot = warn ? "#f87171" : "#6e8cff";
  const band = warn ? "rgba(248, 113, 113, 0.42)" : "rgba(110, 140, 255, 0.5)";
  const value = warn ? "#f87171" : "#93bbfc";

  return (
    <svg
      width={width}
      height="86"
      viewBox={`0 0 ${width} 86`}
      role="img"
      aria-label={`${labels[0]} ${previous.rate}%, ${labels[1]} ${current.rate}%`}
    >
      <text x="0" y="22" fill="#737373" fontSize="11">
        {labels[0]}
      </text>
      <line
        x1={x(previous.ci_low)}
        y1="18"
        x2={x(previous.ci_high)}
        y2="18"
        stroke="#5b6274"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1={x(previous.ci_low)}
        y1="12"
        x2={x(previous.ci_low)}
        y2="24"
        stroke="#5b6274"
        strokeWidth="1.5"
      />
      <line
        x1={x(previous.ci_high)}
        y1="12"
        x2={x(previous.ci_high)}
        y2="24"
        stroke="#5b6274"
        strokeWidth="1.5"
      />
      <circle cx={x(previous.rate)} cy="18" r="4" fill="#8c8c8c" />
      <text x={x(previous.ci_high) + 8} y="22" fill="#8c8c8c" fontSize="11">
        {previous.rate}%
      </text>

      <text x="0" y="48" fill="#737373" fontSize="11">
        {labels[1]}
      </text>
      <line
        x1={x(current.ci_low)}
        y1="44"
        x2={x(current.ci_high)}
        y2="44"
        stroke={band}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1={x(current.ci_low)}
        y1="38"
        x2={x(current.ci_low)}
        y2="50"
        stroke={band}
        strokeWidth="1.5"
      />
      <line
        x1={x(current.ci_high)}
        y1="38"
        x2={x(current.ci_high)}
        y2="50"
        stroke={band}
        strokeWidth="1.5"
      />
      <circle cx={x(current.rate)} cy="44" r="4.5" fill={dot} />
      <text x={x(current.ci_high) + 8} y="48" fill={value} fontSize="11" fontWeight="600">
        {current.rate}%
      </text>

      <line x1={plotX} y1="64" x2={plotX + plotW} y2="64" stroke="rgba(255,255,255,0.1)" />
      <text x={plotX} y="80" fill="#5f5f5f" fontSize="10" textAnchor="middle">
        {pct(min)}
      </text>
      <text x={plotX + plotW / 2} y="80" fill="#5f5f5f" fontSize="10" textAnchor="middle">
        {pct((min + max) / 2)}
      </text>
      <text x={plotX + plotW} y="80" fill="#5f5f5f" fontSize="10" textAnchor="middle">
        {pct(max)}
      </text>
    </svg>
  );
}

/** The same two bars, shrunk to fit a table cell. */
function MiniIntervals({previous, current}) {
  if (!previous || !current) return null;
  if (previous.rate === null || current.rate === null) return null;
  const lo = Math.min(previous.ci_low, current.ci_low);
  const hi = Math.max(previous.ci_high, current.ci_high);
  const pad = (hi - lo) * 0.08 || Math.max(hi * 0.08, 0.0005);
  const min = Math.max(0, lo - pad);
  const max = hi + pad;
  const x = (value) => 4 + ((value - min) / (max - min)) * 142;
  return (
    <svg
      width="150"
      height="34"
      viewBox="0 0 150 34"
      role="img"
      aria-label={`previous ${previous.rate}% versus ${current.rate}%, with 95% intervals`}
    >
      <line
        x1={x(previous.ci_low)}
        y1="11"
        x2={x(previous.ci_high)}
        y2="11"
        stroke="#5b6274"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx={x(previous.rate)} cy="11" r="3" fill="#8c8c8c" />
      <line
        x1={x(current.ci_low)}
        y1="24"
        x2={x(current.ci_high)}
        y2="24"
        stroke="rgba(110, 140, 255, 0.45)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx={x(current.rate)} cy="24" r="3.5" fill="#6e8cff" />
    </svg>
  );
}

const SIGNAL_LEGEND = [
  {
    key: "simplified_switched",
    color: "#4c8bf5",
    name: "Switched to simplified",
    detail: "Saw the new splash and turned it off. One-way: switching back is not reported.",
  },
  {
    key: "simplified_enabled",
    color: "#5b6274",
    name: "Already simplified — excluded",
    detail: "Carried the setting in from an earlier release, so never saw this splash.",
  },
  {
    key: "nosplash",
    color: "#3a3f4b",
    name: "Splash disabled — control",
    detail: "-Dnosplash=true. Says nothing about the art; it moves only when the population does.",
  },
];

/**
 * The three signals as slices of one population.
 *
 * Four cards in a row said nothing about how the numbers relate, and the
 * relation is the whole point: two of the three never saw the artwork, so only
 * the third can be a reaction to it. All three are under one percent, so the
 * remainder is drawn once at true scale and then magnified — a stacked bar at
 * honest scale would be a flat grey line.
 */
function SignalSplit({signals, width = 420}) {
  const parts = SIGNAL_LEGEND.map((entry) => signals?.[entry.key]?.rate);
  if (parts.some((rate) => rate === null || rate === undefined)) return null;

  const [switched, excluded, disabled] = parts;
  const minority = switched + excluded + disabled;
  if (!(minority > 0) || minority >= 100) return null;

  const sliver = Math.max((minority / 100) * width, 4);
  const inner = width - 4;
  const wDisabled = (disabled / minority) * inner;
  const wExcluded = (excluded / minority) * inner;
  const xExcluded = wDisabled + 2;
  const xSwitched = xExcluded + wExcluded + 2;
  const wSwitched = width - xSwitched;

  const segments = [
    {x: 0, w: wDisabled, fill: "#3a3f4b", rate: disabled, label: "#8c8c8c"},
    {x: xExcluded, w: wExcluded, fill: "#5b6274", rate: excluded, label: "#8c8c8c"},
    {x: xSwitched, w: wSwitched, fill: "#4c8bf5", rate: switched, label: "#93bbfc"},
  ];

  return (
    <svg
      width="100%"
      height="132"
      viewBox={`0 0 ${width} 132`}
      preserveAspectRatio="xMinYMin meet"
      role="img"
      aria-label={`${pct(100 - minority)} kept the splash; the remaining ${pct(minority)} splits into ${pct(disabled)} disabled, ${pct(excluded)} already simplified and ${pct(switched)} switched`}
    >
      <rect x="0" y="0" width={width - sliver} height="26" rx="3" fill="#2f3136" />
      <rect x={width - sliver} y="0" width={sliver} height="26" rx="1.5" fill="#4c8bf5" />
      <text x="12" y="17" fill="#8c8c8c" fontSize="11">
        Saw it and kept it &mdash; {pct(100 - minority)}
      </text>

      <polygon
        points={`${width - sliver},28 ${width},28 ${width},58 0,58`}
        fill="rgba(255,255,255,0.05)"
      />
      <line
        x1={width - sliver}
        y1="28"
        x2="0"
        y2="58"
        stroke="rgba(255,255,255,0.14)"
        strokeWidth="1"
      />
      <line x1={width} y1="28" x2={width} y2="58" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
      <text x={width} y="50" fill="#5f5f5f" fontSize="10" textAnchor="end">
        {pct(minority)}, magnified
      </text>

      {segments.map((segment) => (
        <rect
          key={segment.x}
          x={segment.x}
          y="58"
          width={Math.max(segment.w, 1)}
          height="30"
          rx="3"
          fill={segment.fill}
        />
      ))}

      {/* A tick only where the segment is wide enough to carry a number. */}
      {segments
        .filter((segment) => segment.w >= 34)
        .map((segment) => (
          <g key={`tick-${segment.x}`}>
            <line
              x1={segment.x + segment.w / 2}
              y1="92"
              x2={segment.x + segment.w / 2}
              y2="100"
              stroke="#404040"
              strokeWidth="1"
            />
            <text
              x={segment.x + segment.w / 2}
              y="114"
              fill={segment.label}
              fontSize="11"
              textAnchor="middle"
              fontWeight={segment.fill === "#4c8bf5" ? "600" : "400"}
            >
              {pct(segment.rate)}
            </text>
          </g>
        ))}
    </svg>
  );
}

/**
 * One conclusion, with the evidence for it underneath.
 *
 * The four cards this replaces gave equal weight to a rate, a version string
 * and two rates that are not reactions at all, and left the verdict as a bare
 * phrase. Here the headline rate is the page's subject, and the two plots below
 * show the thing the verdict turns on: whether the control moved too.
 */
function SplashHeadline({latest}) {
  if (!latest) {
    return (
      <div className="admin-analytics-empty-state">
        No closed window yet &mdash; a phase cannot be judged until its 28 days are up.
      </div>
    );
  }

  const switched = latest.signals?.simplified_switched;
  const audienceChanged = latest.headline?.verdict === "audience_changed";
  const controlMoved = Boolean(latest.control?.significant);

  return (
    <div className="admin-analytics-hero">
      <div className="admin-analytics-hero-main">
        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
          <div style={{display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap"}}>
            <span className="admin-analytics-hero-value" title={rateDetail(switched)}>
              {formatRate(switched)}
            </span>
            {formatCount(switched) && (
              <span className="admin-analytics-panel-note">{formatCount(switched)} users</span>
            )}
          </div>
          <span className="admin-analytics-hero-lede">
            of{" "}
            <strong>
              {latest.product} {latest.release_version}
            </strong>{" "}
            users switched to the simplified splash &mdash; the only one of the three signals that
            is a reaction to the artwork.
          </span>
        </div>

        <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
          <span className="admin-analytics-verdict-chip">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="5" stroke="#8c8c8c" strokeWidth="1.3" />
              <path d="M6 3.4v3.2" stroke="#8c8c8c" strokeWidth="1.3" strokeLinecap="round" />
              <circle cx="6" cy="8.6" r="0.75" fill="#8c8c8c" />
            </svg>
            <Verdict
              comparison={latest.headline}
              previousVersion={latest.previous_release_version}
            />
          </span>
          <span className="admin-analytics-panel-note">vs {latest.previous_release_version}</span>
        </div>

        {latest.previous_signals && (
          <div className="admin-analytics-evidence">
            <span className="admin-analytics-card-label">
              {audienceChanged ? "Why the comparison cannot be used" : "Against the previous phase"}
            </span>

            <div style={{display: "flex", flexDirection: "column", gap: 4}}>
              <span className="admin-analytics-evidence-title">
                Switched to simplified &mdash; the reaction
              </span>
              <IntervalPlot
                previous={latest.previous_signals.simplified_switched}
                current={latest.signals.simplified_switched}
                labels={[latest.previous_release_version, latest.release_version]}
              />
            </div>

            <div style={{display: "flex", flexDirection: "column", gap: 4}}>
              <span className="admin-analytics-evidence-title">
                Splash disabled &mdash; the control, which the artwork cannot touch
              </span>
              <IntervalPlot
                previous={latest.previous_signals.nosplash}
                current={latest.signals.nosplash}
                labels={[latest.previous_release_version, latest.release_version]}
                tone={controlMoved ? "warn" : "accent"}
              />
            </div>

            <span className="admin-analytics-panel-note admin-analytics-panel-note-prose">
              {controlMoved
                ? "Both moved. A control that shifts on its own means the two phases were seen by different populations, so the splash can be neither credited nor blamed for the difference."
                : "The control held, so the two phases are comparable. Overlapping bars mean the difference in the reaction is within sampling error."}
            </span>
          </div>
        )}
      </div>

      <div className="admin-analytics-hero-side">
        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
          <span className="admin-analytics-card-label">One audience, three signals</span>
          <span className="admin-analytics-panel-note admin-analytics-panel-note-prose">
            Every {latest.product} {latest.release_version} user falls into exactly one of these.
            Only the blue slice saw the new splash and acted on it.
          </span>
        </div>

        <SignalSplit signals={latest.signals} />

        <div className="admin-analytics-signal-legend">
          {SIGNAL_LEGEND.map((entry) => (
            <div key={entry.key} className="admin-analytics-signal-item">
              <span className="admin-analytics-signal-swatch" style={{background: entry.color}} />
              <div style={{display: "flex", flexDirection: "column", gap: 2}}>
                <span className="admin-analytics-signal-name">{entry.name}</span>
                <span className="admin-analytics-panel-note">{entry.detail}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Latest closed window vs the one before it, for every product separately. */
function ReleaseComparisonTable({rows}) {
  if (!rows || rows.length === 0) return null;
  return (
    <>
      <div className="admin-analytics-panel-note admin-analytics-panel-note-prose">
        Each product&apos;s newest closed phase against its previous phase of the same channel — a
        release splash is judged against the previous release splash, never against the EAP before
        it.
      </div>
      <div className="admin-analytics-table-wrap" style={{marginTop: 18}}>
        <table className="admin-analytics-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Release</th>
              <th className="admin-analytics-numeric-cell">Previous</th>
              <th className="admin-analytics-numeric-cell">Switched</th>
              <th style={{width: 186}}>95% intervals</th>
              <th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.product}>
                <td>{row.product}</td>
                <td>
                  {row.phase_label || row.release_version}
                  <div className="admin-analytics-panel-note">
                    {row.release_version} vs {row.previous_release_version}
                  </div>
                </td>
                <td
                  className="admin-analytics-numeric-cell"
                  title={rateDetail(row.previous_signals?.simplified_switched)}
                >
                  {formatRate(row.previous_signals?.simplified_switched)}
                </td>
                <td
                  className="admin-analytics-numeric-cell"
                  title={rateDetail(row.signals?.simplified_switched)}
                >
                  {formatRate(row.signals?.simplified_switched)}
                </td>
                <td>
                  <MiniIntervals
                    previous={row.previous_signals?.simplified_switched}
                    current={row.signals?.simplified_switched}
                  />
                </td>
                <td>
                  <div style={{display: "flex", flexDirection: "column", gap: 3}}>
                    <Verdict
                      comparison={row.headline}
                      previousVersion={row.previous_release_version}
                    />
                    <span className="admin-analytics-panel-note">
                      {row.control?.significant
                        ? `control moved too: ${formatRate(row.previous_signals?.nosplash)} → ${formatRate(row.signals?.nosplash)}`
                        : "control held"}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-analytics-track-legend">
        <span className="admin-analytics-track-legend-item admin-analytics-panel-note">
          <svg width="30" height="8" viewBox="0 0 30 8" aria-hidden="true">
            <line
              x1="1"
              y1="4"
              x2="29"
              y2="4"
              stroke="#5b6274"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="15" cy="4" r="3" fill="#8c8c8c" />
          </svg>
          previous phase
        </span>
        <span className="admin-analytics-track-legend-item admin-analytics-panel-note">
          <svg width="30" height="8" viewBox="0 0 30 8" aria-hidden="true">
            <line
              x1="1"
              y1="4"
              x2="29"
              y2="4"
              stroke="rgba(110,140,255,0.45)"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="15" cy="4" r="3.5" fill="#6e8cff" />
          </svg>
          this phase
        </span>
        <span className="admin-analytics-panel-note">
          Overlapping bars mean the difference is within sampling error.
        </span>
      </div>
    </>
  );
}

const PHASE_TRACKS = [
  {
    key: "pre",
    name: "Pre-release",
    note: "EAP, Beta and RC builds",
    // Beta is its own artwork and its own track point: the feed files a Beta
    // under the EAP channel, so before splash-phases split it out its numbers
    // were counted inside the EAP sample.
    channels: ["eap", "beta", "rc"],
  },
  {
    key: "release",
    name: "Release",
    note: "stable builds — note the different scale",
    channels: ["release"],
  },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function shortDate(iso) {
  if (!iso) return "";
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return `${MONTHS[month - 1]} ${day}`;
}

/**
 * One channel's phases over time.
 *
 * Six identical panels stacked down the page could not be compared with each
 * other, which is the only thing anyone wants to do with them. Splitting by
 * channel rather than plotting all six on one axis is not cosmetic: `nosplash`
 * runs 8-28% in EAP against 0.5-1% in release, so a single line through both
 * would draw the audience, not the artwork.
 */
function PhaseTrack({track, phases, width = 1000}) {
  const points = phases
    .filter((phase) => typeof phase.totals?.simplified_switched?.rate === "number")
    .slice()
    .sort((a, b) => String(a.date_from).localeCompare(String(b.date_from)));
  if (points.length === 0) return null;

  const plotX = 90;
  const plotW = width - plotX - 60;
  const top = 20;
  const bottom = 130;

  // The first and last points carry a value label centred on them, so they
  // cannot sit on the plot edges: at x = plotX the label ran into the axis.
  const inset = 46;
  const times = points.map((phase) => Date.parse(phase.date_from));
  const [t0, t1] = [Math.min(...times), Math.max(...times)];
  const x = (time) =>
    t1 === t0 ? plotX + plotW / 2 : plotX + inset + ((time - t0) / (t1 - t0)) * (plotW - inset * 2);

  const rates = points.map((phase) => phase.totals.simplified_switched.rate);
  const pad = (Math.max(...rates) - Math.min(...rates)) * 0.35 || Math.max(...rates) * 0.4 || 0.001;
  const {lo, hi} = niceBounds(Math.max(0, Math.min(...rates) - pad), Math.max(...rates) + pad);
  const y = (rate) => bottom - ((rate - lo) / (hi - lo)) * (bottom - top);

  const plotted = points.map((phase, index) => ({
    phase,
    x: x(times[index]),
    y: y(phase.totals.simplified_switched.rate),
  }));

  return (
    <div className="admin-analytics-track">
      <div className="admin-analytics-track-head">
        <span className="admin-analytics-track-name">{track.name}</span>
        <span className="admin-analytics-card-label">{track.note}</span>
      </div>
      <svg
        width="100%"
        height="176"
        viewBox={`0 0 ${width} 176`}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label={`${track.name} phases: ${plotted.map((p) => `${p.phase.label} ${p.phase.totals.simplified_switched.rate}%`).join(", ")}`}
      >
        {[bottom, (bottom + top) / 2, top].map((line) => (
          <line
            key={line}
            x1={plotX}
            y1={line}
            x2={plotX + plotW}
            y2={line}
            stroke="rgba(255,255,255,0.06)"
          />
        ))}
        <text x={plotX - 8} y={bottom + 3} fill="#5f5f5f" fontSize="10" textAnchor="end">
          {pct(lo)}
        </text>
        <text
          x={plotX - 8}
          y={(bottom + top) / 2 + 3}
          fill="#5f5f5f"
          fontSize="10"
          textAnchor="end"
        >
          {pct((lo + hi) / 2)}
        </text>
        <text x={plotX - 8} y={top + 3} fill="#5f5f5f" fontSize="10" textAnchor="end">
          {pct(hi)}
        </text>

        {/* A segment touching an open window is dashed: it is not comparable yet. */}
        {plotted.slice(1).map((point, index) => {
          const from = plotted[index];
          const open = !from.phase.window_complete || !point.phase.window_complete;
          return (
            <line
              key={`seg-${point.phase.label}`}
              x1={from.x}
              y1={from.y}
              x2={point.x}
              y2={point.y}
              stroke="#6e8cff"
              strokeWidth="2"
              strokeDasharray={open ? "5 4" : undefined}
            />
          );
        })}

        {plotted.map((point) => (
          <g key={point.phase.label}>
            {point.phase.window_complete ? (
              <circle cx={point.x} cy={point.y} r="4" fill="#6e8cff" />
            ) : (
              <circle
                cx={point.x}
                cy={point.y}
                r="4.5"
                fill="#171717"
                stroke="#6e8cff"
                strokeWidth="2"
              />
            )}
            <text
              x={point.x}
              y={point.y < top + 16 ? point.y + 18 : point.y - 10}
              fill="#fafafa"
              fontSize="11"
              fontWeight="600"
              textAnchor="middle"
            >
              {point.phase.totals.simplified_switched.rate}%
            </text>
            <text x={point.x} y="150" fill="#8c8c8c" fontSize="11" textAnchor="middle">
              {point.phase.label}
            </text>
            <text x={point.x} y="165" fill="#5f5f5f" fontSize="10" textAnchor="middle">
              {shortDate(point.phase.date_from)} &middot;{" "}
              {point.phase.window_complete ? `${point.phase.build_count} builds` : "partial"}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

/** The products inside one phase, and the builds inside one product. */
function PhaseProducts({group}) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((prev) => ({...prev, [id]: !prev[id]}));

  return (
    <div className="admin-analytics-nested-table">
      <table className="admin-analytics-table">
        <thead>
          <tr>
            <th style={{paddingLeft: 22}}>Product</th>
            <th className="admin-analytics-numeric-cell">Switched (reaction)</th>
            <th className="admin-analytics-numeric-cell">Excluded</th>
            <th className="admin-analytics-numeric-cell">Disabled (control)</th>
          </tr>
        </thead>
        <tbody>
          {group.products.map((row) => {
            const builds = row.build_breakdown ?? [];
            const isOpen = Boolean(expanded[row.phase_id]);
            return [
              <tr key={row.phase_id}>
                <td style={{paddingLeft: 22}}>
                  {builds.length > 0 ? (
                    <button
                      className="admin-analytics-phase-toggle"
                      onClick={() => toggle(row.phase_id)}
                    >
                      <Chevron open={isOpen} color={isOpen ? "#93bbfc" : "#737373"} /> {row.product}
                    </button>
                  ) : (
                    row.product
                  )}
                  <div className="admin-analytics-panel-note">
                    {row.builds.length} builds · latest {row.release_version}
                  </div>
                </td>
                <td
                  className="admin-analytics-numeric-cell"
                  title={rateDetail(row.signals?.simplified_switched)}
                >
                  {formatRate(row.signals?.simplified_switched)}
                  {formatCount(row.signals?.simplified_switched) && (
                    <div className="admin-analytics-panel-note">
                      {formatCount(row.signals?.simplified_switched)}
                    </div>
                  )}
                </td>
                <td
                  className="admin-analytics-numeric-cell"
                  title={rateDetail(row.signals?.simplified_enabled)}
                >
                  {formatRate(row.signals?.simplified_enabled)}
                </td>
                <td
                  className="admin-analytics-numeric-cell"
                  title={rateDetail(row.signals?.nosplash)}
                >
                  {formatRate(row.signals?.nosplash)}
                </td>
              </tr>,
              ...(isOpen
                ? builds.map((build) => (
                    <tr
                      key={`${row.phase_id}-${build.build}`}
                      className="admin-analytics-build-row"
                    >
                      <td
                        className="admin-analytics-build-cell"
                        title="A marker of when the switching happened, not a measurement of this build: every build in a phase carries the same splash, and a build released late in the window has had less exposure than the first one."
                      >
                        {build.version}
                        <div className="admin-analytics-panel-note">
                          {build.build} · {build.date}
                        </div>
                      </td>
                      <td className="admin-analytics-numeric-cell">
                        {formatUsers(build.signals?.simplified_switched)}
                      </td>
                      <td className="admin-analytics-numeric-cell">
                        {formatUsers(build.signals?.simplified_enabled)}
                      </td>
                      <td className="admin-analytics-numeric-cell">
                        {formatUsers(build.signals?.nosplash)}
                      </td>
                    </tr>
                  ))
                : []),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Every phase against every product, on one shared bar scale.
 *
 * This is the detail the six panels used to carry, folded behind a disclosure:
 * the tracks above answer "did this artwork do worse than the last one", and
 * only once that has an answer does anyone ask which product's colouring
 * differed.
 */
function PhaseMatrix({groups}) {
  const [openPhase, setOpenPhase] = useState(null);

  const products = [
    ...new Set(groups.flatMap((group) => group.products.map((row) => row.product))),
  ].sort();
  const rates = groups
    .flatMap((group) => group.products.map((row) => row.signals?.simplified_switched?.rate))
    .filter((rate) => typeof rate === "number");
  const scale = rates.length > 0 ? Math.max(...rates) : 1;

  const rateFor = (group, product) =>
    group.products.find((row) => row.product === product)?.signals?.simplified_switched;

  return (
    <table className="admin-analytics-table">
      <thead>
        <tr>
          <th style={{width: 268}}>Phase</th>
          <th className="admin-analytics-numeric-cell" style={{width: 130}}>
            All products
          </th>
          {products.map((product) => (
            <th key={product} className="admin-analytics-numeric-cell">
              {product}
            </th>
          ))}
          <th style={{width: 28}} />
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => {
          const isOpen = openPhase === group.label;
          const total = group.totals?.simplified_switched;
          return [
            <tr
              key={group.label}
              className={isOpen ? "admin-analytics-matrix-row-latest" : undefined}
              onClick={() => setOpenPhase(isOpen ? null : group.label)}
              style={{cursor: "pointer"}}
            >
              <td style={isOpen ? {borderBottom: "none"} : undefined}>
                <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
                  <span className="admin-analytics-matrix-phase">{group.label}</span>
                  {!group.window_complete && (
                    <span className="admin-analytics-matrix-tag">partial</span>
                  )}
                  <SplashWarning products={group.products} />
                </div>
                <div className="admin-analytics-panel-note">
                  {shortDate(group.date_from)} – {shortDate(group.date_to)} · {group.product_count}{" "}
                  products · {group.build_count} builds
                </div>
              </td>
              <td
                className="admin-analytics-numeric-cell"
                style={isOpen ? {borderBottom: "none"} : undefined}
                title={rateDetail(total)}
              >
                <span className="admin-analytics-matrix-total">{formatRate(total)}</span>
                {formatCount(total) && (
                  <div className="admin-analytics-panel-note">{formatCount(total)}</div>
                )}
              </td>
              {products.map((product) => {
                const signal = rateFor(group, product);
                const rate = signal?.rate;
                return (
                  <td
                    key={product}
                    className="admin-analytics-numeric-cell"
                    style={isOpen ? {borderBottom: "none"} : undefined}
                    title={rateDetail(signal)}
                  >
                    {typeof rate === "number" ? (
                      <>
                        <div>{rate}%</div>
                        <svg width="72" height="4" viewBox="0 0 72 4" aria-hidden="true">
                          <rect width="72" height="4" rx="2" fill="rgba(255,255,255,0.05)" />
                          <rect
                            width={Math.max((rate / scale) * 72, 1)}
                            height="4"
                            rx="2"
                            fill="#4c8bf5"
                          />
                        </svg>
                      </>
                    ) : (
                      <span className="admin-analytics-matrix-missing">–</span>
                    )}
                  </td>
                );
              })}
              <td
                className="admin-analytics-numeric-cell"
                style={isOpen ? {borderBottom: "none"} : undefined}
              >
                <Chevron open={isOpen} color={isOpen ? "#93bbfc" : "#737373"} />
              </td>
            </tr>,
            ...(isOpen
              ? [
                  <tr key={`${group.label}-detail`} className="admin-analytics-matrix-row-latest">
                    <td colSpan={products.length + 3} style={{padding: "0 12px 14px"}}>
                      <PhaseProducts group={group} />
                    </td>
                  </tr>,
                ]
              : []),
          ];
        })}
      </tbody>
    </table>
  );
}

function PhaseTracksPanel({groups}) {
  const tracks = PHASE_TRACKS.map((track) => ({
    track,
    phases: groups.filter((group) => track.channels.includes(group.channel)),
  })).filter((entry) => entry.phases.length > 0);

  return (
    <div className="admin-analytics-chart-section">
      <div className="admin-analytics-chart-title">Release phases</div>
      <div className="admin-analytics-panel-note admin-analytics-panel-note-prose">
        One artwork ships per phase, so the phase total is the answer. A phase is only ever judged
        against the previous phase of its own channel — the two tracks below never meet, which is
        why the fourfold gap between pre-release and release audiences is not a trend.
      </div>

      <div style={{marginTop: 22}}>
        {tracks.map(({track, phases}) => (
          <PhaseTrack key={track.key} track={track} phases={phases} />
        ))}
      </div>

      <div className="admin-analytics-track-legend">
        <span className="admin-analytics-track-legend-item admin-analytics-panel-note">
          <svg width="30" height="8" viewBox="0 0 30 8" aria-hidden="true">
            <line x1="1" y1="4" x2="29" y2="4" stroke="#6e8cff" strokeWidth="2" />
          </svg>
          closed window
        </span>
        <span className="admin-analytics-track-legend-item admin-analytics-panel-note">
          <svg width="30" height="8" viewBox="0 0 30 8" aria-hidden="true">
            <line
              x1="1"
              y1="4"
              x2="29"
              y2="4"
              stroke="#6e8cff"
              strokeWidth="2"
              strokeDasharray="5 4"
            />
          </svg>
          window still open — not comparable yet
        </span>
        <span className="admin-analytics-panel-note">
          All products combined; one artwork ships per phase.
        </span>
      </div>
    </div>
  );
}

/**
 * A section the reader opens on purpose.
 *
 * With the phase tracks answering the standing question at the top, everything
 * below is a follow-up. Each fold keeps its headline figures in the summary
 * row, so a closed page still reports the numbers — only the working is folded.
 */
function Fold({title, teaser, children}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`admin-analytics-fold ${open ? "admin-analytics-fold-open" : ""}`}>
      <button
        className="admin-analytics-fold-summary"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
      >
        <span className="admin-analytics-fold-title">
          <Chevron open={open} color={open ? "#93bbfc" : "#8c8c8c"} size={14} />
          {title}
        </span>
        {teaser && <span className="admin-analytics-fold-teaser">{teaser}</span>}
      </button>
      {open && <div className="admin-analytics-fold-body">{children}</div>}
    </div>
  );
}

const VERDICT_SHORT = {
  rising: "rising",
  falling: "falling",
  noise: "no trend",
  audience_changed: "not comparable",
};

/** "2 not comparable · 1 no trend" — the table's conclusions without the table. */
function verdictTeaser(rows) {
  const counts = new Map();
  rows.forEach((row) => {
    const label = VERDICT_SHORT[row.headline?.verdict] ?? VERDICT_SHORT.noise;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  });
  return [...counts].map(([label, count]) => `${count} ${label}`).join(" · ");
}

/**
 * Splash reception per release, from the Analytics Platform (FUS). The three
 * signals stay apart on purpose: only the first is a reaction to the splash
 * that shipped.
 */
function SplashArtifactPanel({data, loading, onRefresh, refreshing}) {
  const [methodOpen, setMethodOpen] = useState(false);

  if (loading) {
    return <div className="admin-analytics-loading">Loading artifact statistics...</div>;
  }
  // An absent payload is not the same as an unconfigured one: the request may
  // have failed, or the server may predate this endpoint. Saying "not
  // configured" for either sends the reader looking for a token they already set.
  if (!data) {
    return (
      <div className="admin-analytics-empty-state">
        Could not load artifact statistics. Check that the server exposes /analytics/artifacts —
        `bun run dev` proxies it to staging, which does not.
      </div>
    );
  }
  const rows = data.by_release ?? [];
  const phaseGroups = data.phase_groups ?? [];
  const comparisons = data.comparisons ?? [];
  const trendCharts = releaseTrendCharts(rows);
  const productCount = new Set(
    phaseGroups.flatMap((group) => group.products.map((row) => row.product))
  ).size;

  return (
    <div className="admin-analytics-splash">
      {/* The method used to be five lines of 12px prose across the full width,
          above everything it qualifies. It is a footnote: one line of facts,
          and the reasoning a click away. */}
      <div className="admin-analytics-meta-strip">
        <div className="admin-analytics-meta-facts">
          {data.updated_at ? (
            <>
              <span className="admin-analytics-panel-note">
                {data.window_days}-day window · closed phases only · same product and channel
              </span>
              <span className="admin-analytics-meta-sep">|</span>
              <span className="admin-analytics-panel-note">
                snapshot {new Date(data.updated_at).toLocaleString()}
              </span>
              <span className="admin-analytics-meta-sep">|</span>
              <button
                className="admin-analytics-method-toggle"
                onClick={() => setMethodOpen((open) => !open)}
                aria-expanded={methodOpen}
              >
                How this is measured
                <svg
                  className={`admin-analytics-method-chevron ${methodOpen ? "admin-analytics-method-chevron-open" : ""}`}
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 3.5 5 6.5 8 3.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </>
          ) : (
            <span className="admin-analytics-panel-note">No snapshot taken yet</span>
          )}
        </div>
        {data.configured && (
          <button
            className="admin-analytics-range-button"
            onClick={onRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        )}
      </div>

      {methodOpen && (
        <div className="admin-analytics-method-body">
          <span className="admin-analytics-panel-note admin-analytics-panel-note-prose">
            Each phase is measured over {data.window_days} days from its first build; only closed
            windows are compared, and only against the same product and channel. Builds are grouped
            by release phase, which is the unit one artwork is designed for — where a phase turns
            out to have shipped more than one image, the panel says so. A move is called a trend
            only when the difference clears the sampling error; hover a rate or a verdict for the
            numbers.
          </span>
        </div>
      )}

      {!data.configured && (
        <div className="admin-analytics-empty-state">
          Analytics Platform access is not configured. Set FUS_ANALYTICS_TOKEN to a Hub token scoped
          for Hub + FUS Analytics (see YouTrack article DO-A-761).
        </div>
      )}

      {data.configured && rows.length === 0 && (
        <div className="admin-analytics-empty-state">
          No snapshots cached yet. Run Refresh — a full pass takes many minutes.
        </div>
      )}

      {/* The standing question is whether this phase's artwork did worse than
          the last one of its channel, so the tracks are the page, not a section
          of it. Everything below answers a follow-up and stays folded. */}
      {phaseGroups.length > 0 && <PhaseTracksPanel groups={phaseGroups} />}

      {data.configured && data.latest && (
        <Fold
          title="Latest closed release"
          teaser={`${data.latest.product} ${data.latest.release_version} · ${formatRate(
            data.latest.signals?.simplified_switched
          )} switched · ${VERDICT_SHORT[data.latest.headline?.verdict] ?? VERDICT_SHORT.noise}`}
        >
          <SplashHeadline latest={data.latest} />
        </Fold>
      )}

      {trendCharts.length > 0 && (
        <Fold
          title="Release over release"
          teaser={trendCharts
            .map((chart) => `${chart.product} ${chart.series[chart.series.length - 1].rate}%`)
            .join(" · ")}
        >
          <SplashTrendCharts rows={rows} />
        </Fold>
      )}

      {comparisons.length > 0 && (
        <Fold title="Latest phase vs previous" teaser={verdictTeaser(comparisons)}>
          <ReleaseComparisonTable rows={comparisons} />
        </Fold>
      )}

      {phaseGroups.length > 0 && (
        <Fold
          title="Breakdown by product"
          teaser={`${phaseGroups.length} phases × ${productCount} products · switched, excluded and control per build`}
        >
          <PhaseMatrix groups={phaseGroups} />
        </Fold>
      )}
    </div>
  );
}

const PRIMARY_TABS = [
  {key: "user", label: "User Activity"},
  {key: "system", label: "System Health"},
  {key: "artifacts", label: "Artifacts"},
];

// Sub-tabs per primary tab. Keyed by primary tab so adding one does not mean
// adding another branch to every render guard below.
const SUB_TABS = {
  user: [
    {key: "users", label: "Per User"},
    {key: "product_channel", label: "Per Product / Channel"},
    {key: "channel", label: "By Channel"},
  ],
  system: [
    {key: "health", label: "API Endpoints"},
    {key: "exports", label: "Exports"},
    {key: "renderer", label: "Renderer"},
    {key: "events", label: "All Events"},
  ],
  artifacts: [{key: "splash", label: "Splash"}],
};

const DEFAULT_SUB_TABS = {user: "users", system: "health", artifacts: "splash"};

const AnalyticsPage = () => {
  const [days, setDays] = useState(365);
  const [primaryTab, setPrimaryTab] = useState("user");
  // One sub-tab position per primary tab, so switching between primary tabs
  // preserves each side's last position.
  const [subTabByPrimary, setSubTabByPrimary] = useState(DEFAULT_SUB_TABS);

  const {data: summary, isLoading: summaryLoading, error: summaryError} = useAnalyticsSummary(days);
  const {data: daily, isLoading: dailyLoading, error: dailyError} = useAnalyticsDaily(days);
  const {data: rendererHealth, isLoading: rendererHealthLoading} = useRendererHealth();
  const {
    data: artifactStats,
    isLoading: artifactStatsLoading,
    error: artifactStatsError,
  } = useArtifactStats();
  const refreshArtifactStats = useRefreshArtifactStats();

  const isArtifactTab = primaryTab === "artifacts";
  // The artifact tab reads its own endpoint, so it must not wait on the
  // dashboard queries, nor surface their errors.
  const loading = isArtifactTab ? false : summaryLoading || dailyLoading;
  const error = isArtifactTab ? artifactStatsError : summaryError || dailyError;
  const subTabs = SUB_TABS[primaryTab];
  const activeSubTab = subTabByPrimary[primaryTab];
  const setActiveSubTab = (key) => setSubTabByPrimary((prev) => ({...prev, [primaryTab]: key}));

  return (
    <div className="admin-analytics-container">
      <div className="admin-analytics-page-header">
        <h1 className="admin-analytics-page-title">Analytics</h1>
        <div className="admin-analytics-range-selector">
          {RANGES.map((r) => (
            <button
              key={r.days}
              className={`admin-analytics-range-button ${days === r.days ? "admin-analytics-range-button-active" : ""}`}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <ExclusionNote emails={daily?.excluded_emails} />

      <div className="admin-analytics-primary-tabs">
        {PRIMARY_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`admin-analytics-primary-tab ${primaryTab === tab.key ? "admin-analytics-primary-tab-active" : ""}`}
            onClick={() => setPrimaryTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="admin-analytics-error-message">
          {error.message || "Failed to load analytics data"}
        </div>
      )}

      {loading ? (
        <div className="admin-analytics-loading">Loading analytics...</div>
      ) : (
        <>
          {primaryTab === "user" && (
            <>
              <UserActivityCards summary={summary} daily={daily} />
              {daily?.daily && <DailyChart data={daily.daily} />}
            </>
          )}
          {primaryTab === "system" && <SystemHealthCards summary={summary} />}

          <div className="admin-analytics-tables-section">
            <div className="admin-analytics-tab-bar">
              {subTabs.map((tab) => (
                <button
                  key={tab.key}
                  className={`admin-analytics-tab-button ${activeSubTab === tab.key ? "admin-analytics-tab-button-active" : ""}`}
                  onClick={() => setActiveSubTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeSubTab === "users" && <TopUsersTable data={daily?.top_users} />}
            {activeSubTab === "product_channel" && (
              <ByProductChannelTable data={daily?.by_product_channel} />
            )}
            {activeSubTab === "channel" && <ByChannelTable data={daily?.by_channel} />}
            {activeSubTab === "health" && <ApiHealthTable data={daily?.api_health} />}
            {activeSubTab === "exports" && <ExportHealthTable data={daily?.export_health} />}
            {activeSubTab === "renderer" &&
              (rendererHealthLoading ? (
                <div className="admin-analytics-loading">Loading renderer health...</div>
              ) : (
                <RendererHealthPanel data={rendererHealth} />
              ))}
            {activeSubTab === "events" && <TopEventsTable data={daily?.top_events} />}
            {activeSubTab === "splash" && (
              <SplashArtifactPanel
                data={artifactStats}
                loading={artifactStatsLoading}
                onRefresh={() => refreshArtifactStats.mutate()}
                refreshing={refreshArtifactStats.isPending}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
